import React, { useState, useCallback, memo } from 'react';
import { FaStar, FaStarHalfAlt, FaRegStar } from 'react-icons/fa';
import axios from 'axios';
import { BASE_URL, USER_URL } from '../../constant/api';

const ProfileRateUs = () => {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [review, setReview] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const token = localStorage.getItem('token');

  const handleStarClick = useCallback((value) => {
    setRating(value);
  }, []);

  const handleMouseEnter = useCallback((value) => {
    setHoverRating(value);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setHoverRating(0);
  }, []);

  // Memoized star rendering logic for better performance
  const renderStar = useCallback((index) => {
    const currentRating = hoverRating || rating;
    const isFullStar = currentRating >= index;
    const isHalfStar = currentRating >= index - 0.5;

    const commonClasses = "transition-transform transform hover:scale-110";

    // Adjust font size based on screen size
    const starSize = "text-4xl md:text-5xl";

    if (isFullStar) {
      return <FaStar className={`text-yellow-500 ${commonClasses} ${starSize}`} />;
    } else if (isHalfStar) {
      return <FaStarHalfAlt className={`text-yellow-500 ${commonClasses} ${starSize}`} />;
    } else {
      return <FaRegStar className={`text-gray-300 ${commonClasses} ${starSize}`} />;
    }
  }, [hoverRating, rating]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;
    if (rating === 0) {
      alert('Please select a rating before submitting.');
      return;
    }

    setIsSubmitting(true);

    try {
      await USER_URL.post(
        `/accounts/app-feedback/create/`,
        { rating, review: review.trim() },

      );

      setShowModal(true);
      // Reset form after successful submission
      setRating(0);
      setReview('');
    } catch (error) {
      alert('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReviewChange = useCallback((e) => {
    setReview(e.target.value);
  }, []);

  const closeModal = useCallback(() => {
    setShowModal(false);
  }, []);

  // Memoized Modal component
  const FeedbackModal = memo(() => (
    <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50 z-50">
      <div className="bg-white rounded-lg p-6 text-center max-w-md mx-4">
        <h2 className="text-2xl font-semibold">Thank You!</h2>
        <p className="mt-2">Your feedback has been submitted successfully.</p>
        <button
          onClick={closeModal}
          className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400"
        >
          Close
        </button>
      </div>
    </div>
  ));

  return (
    <div className="bg-white rounded-2xl p-4 min-h-screen">
      <div className="flex flex-col w-full max-w-2xl mx-auto">
        <div>
          <h1 className="text-center text-base md:text-4xl font-semibold p-5">RATE US</h1>
        </div>

        <div className="p-4 font-semibold text-center">
          <p className="text-sm md:text-3xl">Are you satisfied with</p>
          <p className="text-sm md:text-3xl text-green-700">Bricksnmix</p>
        </div>

        <div className="flex flex-row items-center justify-center space-x-4" role="group" aria-label="Star rating">
          {[1, 2, 3, 4, 5].map((index) => (
            <div
              key={index}
              className="cursor-pointer relative"
              onMouseEnter={() => handleMouseEnter(index)}
              onMouseLeave={handleMouseLeave}
              onClick={() => handleStarClick(index)}
              role="button"
              tabIndex={0}
              aria-label={`Rate ${index} stars`}
            >
              {renderStar(index)}
              <div
                className="absolute inset-y-0 left-0 w-1/2 overflow-hidden"
                onMouseEnter={() => handleMouseEnter(index - 0.5)}
                onClick={(e) => {
                  e.stopPropagation();
                  handleStarClick(index - 0.5);
                }}
                role="button"
                tabIndex={0}
                aria-label={`Rate ${index - 0.5} stars`}
              />
            </div>
          ))}
        </div>

        <div className="text-center text-base md:text-lg mt-4 font-medium" aria-live="polite">
          {rating > 0 ? `You selected: ${rating} star${rating === 1 ? '' : 's'}` : 'Select a rating'}
        </div>

        <div className="p-1 md:p-10 mx-5">
          <form onSubmit={handleSubmit} className="w-full p-0 md:px-10">
            <div className="grid grid-cols-1 gap-3">
              <div>
                <label htmlFor="customer_review" className="block text-sm font-medium text-gray-700">
                Review
                </label>
                <textarea
                  name="customer_review"
                  id="customer_review"
                  value={review}
                  onChange={handleReviewChange}
                  className="mt-1 block w-full border border-gray-300 p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Write your review here..."
                  rows={4}
                  maxLength={1000}
                />
              </div>
            </div>

            <div className="flex flex-row mt-4 space-x-4">
              <button
                type="button"
                className="mt-4 text-gray-500 border border-gray-300 w-full lg:w-1/2 p-2 rounded hover:bg-gray-100 transition duration-300 focus:outline-none focus:ring-2 focus:ring-gray-400"
              >
                Later
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className={`mt-4 text-black border border-brand-primary w-full lg:w-1/2 p-2 rounded transition duration-300 focus:outline-none focus:ring-2 focus:ring-brand-primary
                  ${isSubmitting ? 'opacity-50 cursor-not-allowed' : 'hover:bg-brand-primary hover:text-white'}`}
              >
                {isSubmitting ? 'Submitting...' : 'Rate'}
              </button>
            </div>
          </form>
        </div>

        {showModal && <FeedbackModal />}
      </div>
    </div>
  );
};

export default ProfileRateUs;